import React, { useEffect, useState, useMemo } from "react";
import {
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useSnackbar } from "notistack";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import moment from "moment";
import * as XLSX from "xlsx";
import { get_applications_api } from "../../DAL/Careers/Careers";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    color: "#1a1a1a",
    fontSize: "14px",
    "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: "#1a1a1a" },
  "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

const selectSx = {
  backgroundColor: "#ffffff",
  borderRadius: "9px",
  color: "#1a1a1a",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.16)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.28)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#c01e2e", borderWidth: "1.5px" },
  "& .MuiSvgIcon-root": { color: "#c01e2e" },
};

const STATUS_OPTIONS = ["new", "reviewing", "interviewing", "offered", "rejected", "hired"];

const getStatusChipSx = (status) => {
  switch (status) {
    case "new": return { backgroundColor: "rgba(37,99,235,0.1)", color: "#2563eb" };
    case "reviewing":
    case "interviewing": return { backgroundColor: "rgba(217,119,6,0.1)", color: "#d97706" };
    case "offered":
    case "hired": return { backgroundColor: "rgba(22,163,74,0.1)", color: "#16a34a" };
    case "rejected": return { backgroundColor: "rgba(192,30,46,0.1)", color: "#c01e2e" };
    default: return { backgroundColor: "rgba(26,26,26,0.06)", color: "#6b6b6b" };
  }
};

export default function ApplicationsList() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [applications, setApplications] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    getApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getApplications = async () => {
    try {
      const result = await get_applications_api({ limit: 500 });
      if (result.code === 200) {
        setApplications(result.data || []);
      } else {
        enqueueSnackbar(result.message || "Failed to load applications", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error fetching applications", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return applications.filter((app) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        searchQuery === "" ||
        app.fullName?.toLowerCase().includes(searchLower) ||
        app.email?.toLowerCase().includes(searchLower) ||
        app.jobTitleSnapshot?.toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === "all" || app.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [applications, searchQuery, statusFilter]);

  const hasActiveFilters = searchQuery !== "" || statusFilter !== "all";
  const clearFilters = () => { setSearchQuery(""); setStatusFilter("all"); };
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };
  const handleViewApplication = (app) => navigate(`/careers/applications/${app._id}`);

  const exportToExcel = () => {
    const exportData = filteredData.map((app) => ({
      Name: app.fullName,
      Email: app.email,
      Phone: app.phone,
      Role: app.jobTitleSnapshot,
      Status: app.status,
      "Salary Expectation": app.salaryExpectation,
      "Right to Work UK": app.rightToWorkUK ? "Yes" : "No",
      "Submitted": app.createdAt ? moment(app.createdAt).format("DD-MM-YYYY HH:mm") : "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applications");
    XLSX.writeFile(workbook, "careers_applications.xlsx");
  };

  return (
    <>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <CircularProgress sx={{ color: "#c01e2e" }} />
        </Box>
      ) : (
        <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "calc(100vh - 48px)" }}>
          <Paper sx={{
            backgroundColor: "#ffffff",
            border: "1px solid rgba(26,26,26,0.09)",
            borderRadius: "14px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            p: 3,
          }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pb: 2.5, borderBottom: "1px solid rgba(26,26,26,0.09)" }}>
              <Box>
                <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>
                  Careers — Applications
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#aaa", fontFamily: "'Rubik', sans-serif", mt: 0.25 }}>
                  {applications.length} total applications
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  variant="outlined"
                  onClick={exportToExcel}
                  sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", borderRadius: "8px",
                    "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.06)" } }}
                >
                  Export
                </Button>
                <Button
                  component={RouterLink}
                  to="/careers/jobs"
                  variant="contained"
                  startIcon={<WorkOutlineIcon sx={{ fontSize: 16 }} />}
                  sx={{ backgroundColor: "#c01e2e", color: "#fff", textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 500, borderRadius: "8px", px: 2.5, boxShadow: "none",
                    "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}
                >
                  Manage Job Postings
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search by name, email, or role…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ ...inputSx, minWidth: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#aaa", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: "#aaa", fontSize: "14px", "&.Mui-focused": { color: "#c01e2e" } }}>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} sx={selectSx}>
                  <MenuItem value="all">All</MenuItem>
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {hasActiveFilters && (
                <Button variant="outlined" size="small" onClick={clearFilters} startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", borderRadius: "8px",
                    "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.06)" } }}>
                  Clear
                </Button>
              )}
              <Box sx={{ ml: "auto" }}>
                <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                  {filteredData.length} of {applications.length}
                </Typography>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Applicant", "Role", "Salary Expectation", "Submitted", "Status"].map((label) => (
                      <TableCell key={label} sx={{ color: "#6b6b6b", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px", fontFamily: "'Rubik', sans-serif", borderBottom: "1px solid rgba(26,26,26,0.09)", backgroundColor: "#F2EDE4" }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((app) => (
                      <TableRow
                        key={app._id}
                        hover
                        onClick={() => handleViewApplication(app)}
                        sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#F7F4EF" }, "& td": { borderBottom: "1px solid rgba(26,26,26,0.06)" } }}
                      >
                        <TableCell>
                          <Box sx={{ color: "#1a1a1a", fontSize: "14px", fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>{app.fullName}</Box>
                          <Box sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{app.email}</Box>
                        </TableCell>
                        <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{app.jobTitleSnapshot}</TableCell>
                        <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>{app.salaryExpectation || "—"}</TableCell>
                        <TableCell sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>
                          {app.createdAt ? moment(app.createdAt).fromNow() : "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={app.status}
                            size="small"
                            sx={{ ...getStatusChipSx(app.status), borderRadius: "6px", fontSize: "11px", fontWeight: 500, fontFamily: "'Rubik', sans-serif", textTransform: "capitalize" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center", color: "#aaa", fontFamily: "'Rubik', sans-serif", py: 4 }}>
                        No applications match these filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ color: "#6b6b6b", fontFamily: "'Rubik', sans-serif", "& .MuiTablePagination-selectIcon": { color: "#c01e2e" }, "& .MuiTablePagination-actions button": { color: "#6b6b6b" } }}
            />
          </Paper>
        </Box>
      )}
    </>
  );
}